import * as React from "react";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  TextField,
} from "@mui/material";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftTypography from "components/SoftTypography";
import { AxiosError } from "axios";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

type DateInput = string | number | Date;

// Table options
const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

interface AssetTypeItem {
  asset_type_id: number;
  asset_type_name: string;
}

interface SubscriptionTypeItem {
  subscription_type_id: number;
  subscription_type_name: string;
}

interface CustomerItem {
  customer_id: number;
  name: string;
}

// Extracted component for status cell rendering
const StatusCell: React.FC<{ value: string }> = ({ value }) => {
  return (
    <span
      className="active-btn"
      style={{
        borderColor: value ? "#00bdaa" : "#fe346e",
      }}
    >
      {value ? "Active" : "Inactive"}
    </span>
  );
};

// Render helper functions - OUTSIDE the component
const renderStatusCell = (value: string) => {
  return <StatusCell value={value} />;
};

const ExpiringSubscriptions = () => {
  const [rows, setRows] = React.useState([]);
  const [error, setError] = React.useState<AxiosError | null>(null);
  const [assetTypes, setAssetTypes] = React.useState<AssetTypeItem[] | null>(null);
  const [subscriptionTypes, setSubscriptionTypes] = React.useState<
    SubscriptionTypeItem[] | null
  >(null);
  const [customers, setCustomers] = React.useState<CustomerItem[] | null>(null);
  const [assetTypeFilter, setAssetTypeFilter] =
    React.useState<AssetTypeItem | null>(null);
  const [subscriptionTypeFilter, setSubscriptionTypeFilter] = React.useState<
    SubscriptionTypeItem | null
  >(null);
  const [customerFilter, setCustomerFilter] =
    React.useState<CustomerItem | null>(null);

  const hasActiveFilters =
    assetTypeFilter != null ||
    subscriptionTypeFilter != null ||
    customerFilter != null;

  const clearFilters = () => {
    setAssetTypeFilter(null);
    setSubscriptionTypeFilter(null);
    setCustomerFilter(null);
  };

  const formatDate = (dateString: DateInput) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "2-digit",
    };

    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", options).format(date);
  };

  const columns: MUIDataTableColumn[] = [
    { name: "name", label: "Customer" },
    { name: "asset_service_id", label: "Asset Id" },
    { name: "asset_type_name", label: "Asset Type" },
    { name: "subscription_type", label: "Subscription Type" },
    {
      name: "start_date",
      label: "Start Date",
      options: {
        filter: false,
        customBodyRender: (value: string | number | Date) => formatDate(value),
      },
    },
    {
      name: "end_date",
      label: "End Date",
      options: {
        filter: false,
        customBodyRender: (value: string | number | Date) => formatDate(value),
      },
    },
    {
      name: "is_active",
      label: "Status",
      options: {
        filter: false,
        customBodyRender: renderStatusCell,
      },
    },
  ];

  const fetchData = React.useCallback(async () => {
    try {
      const response = await axios.get("/subscription", {
        params: {
          filters: {
            asset_type_id: assetTypeFilter?.asset_type_id,
            subscription_type_id: subscriptionTypeFilter?.subscription_type_id,
            customer_id: customerFilter?.customer_id,
          },
        },
      });

      const allSubscriptions = response?.data || [];

      // ---- Filter expiring in next 7 days ----
      const today = new Date();
      const next7 = new Date();
      next7.setDate(today.getDate() + 7);

      const expiring = allSubscriptions.filter((sub: any) => {
        const end = new Date(sub.end_date);
        return end >= today && end <= next7;
      });

      setRows(expiring);
    } catch (error) {
      if (error instanceof AxiosError && error) {
        setError(error);
      }
    }
  }, [assetTypeFilter, subscriptionTypeFilter, customerFilter]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchFiltersData = async () => {
    try {
      const [assetTypesRes, subscriptionTypesRes, customersRes] =
        await Promise.all([
          axios.get("/asset-types"),
          axios.get("/subscription-types"),
          axios.get("/customer"),
        ]);
      setAssetTypes(assetTypesRes?.data?.list ?? []);
      setSubscriptionTypes(subscriptionTypesRes?.data?.list ?? []);
      setCustomers(customersRes?.data?.list ?? []);
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  React.useEffect(() => {
    fetchFiltersData();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <Grid
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-end" }}>
            <FormControl sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                sx={{ paddingBottom: "6px" }}
              >
                Asset Types
              </SoftTypography>
              <Autocomplete
                options={assetTypes ?? []}
                getOptionLabel={(option) => option.asset_type_name ?? ""}
                value={assetTypeFilter}
                onChange={(event, value) => setAssetTypeFilter(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    sx={{
                      "& .MuiInputBase-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                )}
              />
            </FormControl>
            <FormControl sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                sx={{ paddingBottom: "6px" }}
              >
                Subscription Type
              </SoftTypography>
              <Autocomplete
                options={subscriptionTypes ?? []}
                getOptionLabel={(option) => option.subscription_type_name ?? ""}
                value={subscriptionTypeFilter}
                onChange={(event, value) => setSubscriptionTypeFilter(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder="Select type"
                    sx={{
                      "& .MuiInputBase-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                )}
              />
            </FormControl>
            <FormControl sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                sx={{ paddingBottom: "6px" }}
              >
                Customer
              </SoftTypography>
              <Autocomplete
                options={customers ?? []}
                getOptionLabel={(option) => option.name ?? ""}
                value={customerFilter}
                onChange={(event, value) => setCustomerFilter(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder="Select customer"
                    sx={{
                      "& .MuiInputBase-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                )}
              />
            </FormControl>
            {hasActiveFilters && (
              <Button variant="outlined" color="primary" onClick={clearFilters} size="small">
                Clear Filter
              </Button>
            )}
          </Box>
        </Grid>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-subscriptions"
        >
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={"Manage Subscriptions"}
              data={rows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
          {error && <div>Error: {error.message}</div>}
        </SoftBox>
      </Card>
    </DashboardLayout>
  );
};

export default ExpiringSubscriptions;
